import { defineAsyncComponent } from 'vue';
import { SchemaControl, type SchemaControlArgs } from '@/modules/common/models/schema/schema-control.ts';
import { delay } from '@/modules/common/utils/promise.ts';

const RETRY_LIMIT = 2;
const RETRY_DELAY = 500;

type TextEditorModule = typeof import('@/modules/ui/components/ui/UiTextEditor.vue');

let request: Promise<TextEditorModule> | undefined;

async function importTextEditor(attempt = 1): Promise<TextEditorModule> {
  try {
    return await import('@/modules/ui/components/ui/UiTextEditor.vue');
  } catch (error) {
    if (attempt > RETRY_LIMIT) {
      throw error;
    }

    await delay(RETRY_DELAY * attempt);

    return importTextEditor(attempt + 1);
  }
}

function loadTextEditor() {
  request ??= importTextEditor().catch((error) => {
    request = undefined;
    throw error;
  });

  return request;
}

export function preloadTextEditor() {
  void loadTextEditor().catch(() => {});
}

export const LazyUiTextEditor = defineAsyncComponent(loadTextEditor);

type SchemaTextEditorArgs = SchemaControlArgs & {
  placeholder?: string;
  rows?: number;
  maxLength?: number;
  resizable?: boolean;
};

export class SchemaTextEditor extends SchemaControl<string> {
  readonly component = LazyUiTextEditor;
  readonly focusOnLabelClick = true;
  readonly placeholder?: string;
  readonly rows?: number;
  readonly maxLength?: number;
  readonly resizable: boolean;

  constructor(args: SchemaTextEditorArgs) {
    super(args);

    this.placeholder = args.placeholder;
    this.rows = args.rows;
    this.maxLength = args.maxLength;
    this.resizable = args.resizable ?? true;
  }
}
